import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  keyframes,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LaunchIcon from '@mui/icons-material/Launch';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useTranslation } from 'react-i18next';
import GlassDialog from '../ui/GlassDialog';

const SURVEY_1_KEY = 'survey_1_completed';
const SURVEY_2_KEY = 'survey_2_completed';
const SURVEYS_DISMISSED_KEY = 'surveys_dismissed_forever';

const SURVEY_1_URL = 'https://forms.gle/sgMe58G6bNTebp8B6';
const SURVEY_2_URL = 'https://forms.gle/R5UMVS4xZ98yoUru7';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SurveyModal: React.FC = () => {
  const { t } = useTranslation('home');
  const [open, setOpen] = useState(false);
  const [survey1Done, setSurvey1Done] = useState(false);
  const [survey2Done, setSurvey2Done] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(SURVEYS_DISMISSED_KEY) === 'true';
    const s1 = localStorage.getItem(SURVEY_1_KEY) === 'true';
    const s2 = localStorage.getItem(SURVEY_2_KEY) === 'true';

    setSurvey1Done(s1);
    setSurvey2Done(s2);

    if (!dismissed && !(s1 && s2)) {
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleOpenSurvey = (url: string, storageKey: string, setDone: (v: boolean) => void) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    localStorage.setItem(storageKey, 'true');
    setDone(true);

    const otherDone =
      storageKey === SURVEY_1_KEY
        ? localStorage.getItem(SURVEY_2_KEY) === 'true'
        : localStorage.getItem(SURVEY_1_KEY) === 'true';

    if (otherDone) {
      setOpen(false);
    }
  };

  const handleDismissForever = () => {
    localStorage.setItem(SURVEYS_DISMISSED_KEY, 'true');
    setOpen(false);
  };

  const handleClose = () => setOpen(false);

  return (
    <GlassDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          overflow: 'hidden',
          animation: `${fadeIn} 0.4s ease-out`,
          m: { xs: 1.5, sm: 4 },
          width: { xs: 'calc(100% - 24px)', sm: 'auto' },
          maxWidth: { xs: 'calc(100% - 24px)', sm: '600px' },
        },
      }}
    >
      <Box sx={{ position: 'relative', p: { xs: 2.5, sm: 4 } }}>
        <IconButton
          onClick={handleClose}
          aria-label="close"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'rgba(255, 255, 255, 0.6)',
            '&:hover': {
              color: '#fff',
              background: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              mb: 2,
              boxShadow: '0 8px 24px rgba(245, 158, 11, 0.35)',
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 36, color: '#fff' }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              color: '#fff',
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            {t('survey.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: { xs: '0.85rem', sm: '0.95rem' },
              lineHeight: 1.5,
            }}
          >
            {t('survey.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          <SurveyCard
            title={t('survey.survey1Title')}
            done={survey1Done}
            onOpen={() => handleOpenSurvey(SURVEY_1_URL, SURVEY_1_KEY, setSurvey1Done)}
            openLabel={t('survey.open')}
            completedLabel={t('survey.completed')}
          />
          <SurveyCard
            title={t('survey.survey2Title')}
            done={survey2Done}
            onOpen={() => handleOpenSurvey(SURVEY_2_URL, SURVEY_2_KEY, setSurvey2Done)}
            openLabel={t('survey.open')}
            completedLabel={t('survey.completed')}
          />
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Button
            onClick={handleDismissForever}
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.5)',
              textTransform: 'none',
              fontSize: '0.8rem',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.8)',
                background: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            {t('survey.dontShowAgain')}
          </Button>
        </Box>
      </Box>
    </GlassDialog>
  );
};

interface SurveyCardProps {
  title: string;
  done: boolean;
  onOpen: () => void;
  openLabel: string;
  completedLabel: string;
}

const SurveyCard: React.FC<SurveyCardProps> = ({ title, done, onOpen, openLabel, completedLabel }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'stretch', sm: 'center' },
      justifyContent: 'space-between',
      gap: { xs: 1, sm: 2 },
      p: { xs: 1.5, sm: 2 },
      borderRadius: 2,
      background: done ? 'rgba(34, 197, 94, 0.08)' : 'rgba(255, 255, 255, 0.04)',
      border: `1px solid ${done ? 'rgba(34, 197, 94, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
      transition: 'all 0.3s ease',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      {done && <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 20, flexShrink: 0 }} />}
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 600,
          fontSize: { xs: '0.9rem', sm: '1rem' },
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {title}
      </Typography>
    </Box>
    {done ? (
      <Typography
        sx={{
          color: '#22c55e',
          fontSize: '0.8rem',
          fontWeight: 600,
          flexShrink: 0,
          textAlign: { xs: 'center', sm: 'right' },
        }}
      >
        {completedLabel}
      </Typography>
    ) : (
      <Button
        onClick={onOpen}
        variant="contained"
        size="small"
        endIcon={<LaunchIcon sx={{ fontSize: '1rem !important' }} />}
        sx={{
          flexShrink: 0,
          width: { xs: '100%', sm: 'auto' },
          px: 2,
          py: 0.75,
          borderRadius: '8px',
          background: 'rgba(185, 28, 28, 0.15)',
          border: '1px solid rgba(185, 28, 28, 0.5)',
          color: '#fff',
          fontWeight: 600,
          fontSize: { xs: '0.8rem', sm: '0.8rem' },
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'rgba(185, 28, 28, 0.3)',
            borderColor: '#dc2626',
            boxShadow: '0 0 16px rgba(185, 28, 28, 0.4)',
          },
        }}
      >
        {openLabel}
      </Button>
    )}
  </Box>
);

export default SurveyModal;
